import { useEffect } from "react";

/**
 * Normalize browser wheel deltas (lines vs pixels vs pages).
 * @param {WheelEvent} event
 * @param {HTMLElement} el
 */
const normalizeWheelDelta = (event, el) => {
  let { deltaX, deltaY } = event;
  const mode = event.deltaMode;
  if (mode === 1) {
    deltaX *= 16;
    deltaY *= 16;
  } else if (mode === 2) {
    deltaX *= el.clientWidth || 1;
    deltaY *= el.clientHeight || 1;
  }
  return { deltaX, deltaY };
};

/**
 * Lets horizontal strips use native vertical page scroll when the wheel is vertical
 * away from the strip, or when the strip is already at a scroll limit.
 *
 * - Horizontal-dominant wheel / trackpad (deltaX) scrolls the strip.
 * - Shift+wheel maps vertical delta to horizontal (common desktop pattern).
 * - Optional mapWheelYToHorizontal: vertical-dominant wheel over the strip scrolls
 *   horizontally (helps trackpads that report mostly deltaY while swiping over a carousel).
 */
const DEFAULT_AXIS_RATIO = 1.35;

/**
 * @param {React.RefObject<HTMLElement | null>} ref
 * @param {{
 *   axisRatio?: number,
 *   mapWheelYToHorizontal?: boolean,
 *   wheelYVsXRatio?: number,
 * }} [options]
 */
const useHorizontalWheel = (ref, options = {}) => {
  const axisRatio = options.axisRatio ?? DEFAULT_AXIS_RATIO;
  const mapWheelYToHorizontal = options.mapWheelYToHorizontal === true;
  const wheelYVsXRatio = options.wheelYVsXRatio ?? 0.72;

  useEffect(() => {
    const el = ref?.current;
    if (!el) return undefined;

    const onWheel = (event) => {
      if (event.ctrlKey) return;

      if (el.scrollWidth <= el.clientWidth + 1) return;

      const { deltaX: dxRaw, deltaY: dyRaw } = normalizeWheelDelta(event, el);
      let deltaX = dxRaw;
      let deltaY = dyRaw;

      // Shift + vertical wheel → scroll strip horizontally (do not steal normal vertical wheel)
      if (event.shiftKey && deltaY !== 0) {
        const before = el.scrollLeft;
        el.scrollLeft += deltaY;
        if (el.scrollLeft !== before) {
          event.preventDefault();
        }
        return;
      }

      if (mapWheelYToHorizontal) {
        const yDominant =
          deltaY !== 0 &&
          Math.abs(deltaY) >= Math.abs(deltaX) * wheelYVsXRatio;
        if (yDominant) {
          const before = el.scrollLeft;
          el.scrollLeft += deltaY;
          if (el.scrollLeft !== before) {
            event.preventDefault();
          }
          return;
        }
      }

      // Vertical-dominant gesture → leave event for the page scrollbar
      if (Math.abs(deltaX) < Math.abs(deltaY) * axisRatio || deltaX === 0) {
        return;
      }

      const before = el.scrollLeft;
      el.scrollLeft += deltaX;
      if (el.scrollLeft !== before) {
        event.preventDefault();
      }
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [ref, axisRatio, mapWheelYToHorizontal, wheelYVsXRatio]);
};

export default useHorizontalWheel;
